/**
 * Analitzador del subconjunt de Markdown dels missatges del xat d'una activació:
 * `**negreta**`, `*cursiva*`, `~~ratllat~~`, `` `codi` ``, llistes `- ` i `1. ` i salts de línia.
 * Retorna un arbre de dades (mai HTML): el component `TextMarkdown` el converteix en elements React.
 * Els marcadors sense tancar es mostren tal com s'han escrit.
 */

export type Inline =
  | { tipus: 'text'; text: string }
  | { tipus: 'codi'; text: string }
  | { tipus: 'negreta' | 'cursiva' | 'ratllat'; fills: Inline[] };

export type BlocMarkdown =
  | { tipus: 'paragraf'; linies: Inline[][] }
  | { tipus: 'llista'; ordenada: boolean; inici: number; items: Inline[][] };

const RE_PIC = /^\s*-\s+(.*)$/;
const RE_NUM = /^\s*(\d{1,9})[.)]\s+(.*)$/;

const esBlanc = (c: string | undefined) => c === undefined || /\s/.test(c);

function afegeixText(sortida: Inline[], text: string) {
  if (!text) return;
  const darrer = sortida[sortida.length - 1];
  if (darrer?.tipus === 'text') darrer.text += text;
  else sortida.push({ tipus: 'text', text });
}

/** Posició del delimitador de tancament (o -1): contingut no buit i sense espais als extrems. */
function tancament(text: string, desde: number, marca: string): number {
  let j = text.indexOf(marca, desde);
  while (j !== -1) {
    const valid =
      j > desde &&
      !esBlanc(text[j - 1]) &&
      // Un `*` sol no pot formar part d'un `**`.
      (marca !== '*' || (text[j + 1] !== '*' && text[j - 1] !== '*'));
    if (valid) return j;
    j = text.indexOf(marca, j + 1);
  }
  return -1;
}

export function analitzaInline(text: string): Inline[] {
  const sortida: Inline[] = [];
  let i = 0;
  let pendent = '';
  const buida = () => {
    afegeixText(sortida, pendent);
    pendent = '';
  };
  while (i < text.length) {
    const c = text[i];
    if (c === '`') {
      const j = text.indexOf('`', i + 1);
      if (j > i + 1) {
        buida();
        sortida.push({ tipus: 'codi', text: text.slice(i + 1, j) });
        i = j + 1;
        continue;
      }
    } else if (c === '*' || c === '~') {
      const doble = text[i + 1] === c;
      const marca = doble ? c + c : c;
      // `~` sol no és cap marcador; `***` es tracta com a `**` + `*`.
      if (marca !== '~' && !esBlanc(text[i + marca.length])) {
        const j = tancament(text, i + marca.length, marca);
        if (j !== -1) {
          buida();
          const tipus = marca === '**' ? 'negreta' : marca === '~~' ? 'ratllat' : 'cursiva';
          sortida.push({ tipus, fills: analitzaInline(text.slice(i + marca.length, j)) });
          i = j + marca.length;
          continue;
        }
      }
      pendent += marca;
      i += marca.length;
      continue;
    }
    pendent += c;
    i += 1;
  }
  buida();
  return sortida;
}

export function analitzaMarkdown(text: string): BlocMarkdown[] {
  const blocs: BlocMarkdown[] = [];
  let paragraf: string[] = [];
  const tancaParagraf = () => {
    while (paragraf.length && !paragraf[0]?.trim()) paragraf.shift();
    while (paragraf.length && !paragraf[paragraf.length - 1]?.trim()) paragraf.pop();
    if (paragraf.length) {
      blocs.push({ tipus: 'paragraf', linies: paragraf.map(analitzaInline) });
    }
    paragraf = [];
  };
  for (const linia of text.replace(/\r\n?/g, '\n').split('\n')) {
    const pic = RE_PIC.exec(linia);
    const num = pic ? null : RE_NUM.exec(linia);
    if (pic || num) {
      const ordenada = num != null;
      const contingut = pic ? (pic[1] ?? '') : (num?.[2] ?? '');
      const darrer = paragraf.length === 0 ? blocs[blocs.length - 1] : undefined;
      if (darrer?.tipus === 'llista' && darrer.ordenada === ordenada) {
        darrer.items.push(analitzaInline(contingut));
      } else {
        tancaParagraf();
        blocs.push({
          tipus: 'llista',
          ordenada,
          inici: num ? Number(num[1]) : 1,
          items: [analitzaInline(contingut)],
        });
      }
    } else {
      paragraf.push(linia);
    }
  }
  tancaParagraf();
  return blocs;
}
